import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
  type AxiosRequestHeaders,
} from "axios"
import { useAuthStore } from "@/stores/auth"
import router from "@/router"
import { sleep } from "../client"
import { getAuthHeaders } from "./auth"
import { ApiError } from "./errors"

/**
 * Request configuration factory for TanStack Query
 */

let refreshPromise: Promise<void> | null = null

/**
 * Create a configured axios instance for TanStack Query
 */
export function createQueryAxios(): AxiosInstance {
  const instance = axios.create({
    baseURL: "/api",
  })

  // Request interceptor for auth headers and development delay
  instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    // Add development delay to match current behavior
    if (import.meta.env.DEV) {
      await sleep(500)
    }

    // Get auth headers
    const headers = getAuthHeaders()

    // Apply auth headers
    Object.entries(headers).forEach(([key, value]) => {
      if (!config.headers?.[key]) {
        config.headers[key] = value
      }
    })

    return config
  })

  // Response interceptor for token refresh and error parsing
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: any) => {
      const originalRequest = error.config
      const authStore = useAuthStore()
      const hasRefreshToken: boolean = !!authStore.refresh_token

      if (error.response?.status === 401 && hasRefreshToken) {
        if (!originalRequest._retry) {
          try {
            originalRequest._retry = true
            if (!refreshPromise) {
              // Call refresh token action from Pinia store
              refreshPromise = authStore.refreshToken().then(() => {})
            }
            await refreshPromise!

            // Update the request with new token
            const newToken = authStore.token
            originalRequest.headers["Authorization"] = "Bearer " + newToken

            return instance(originalRequest)
          } catch (e: any) {
            // Refresh token failed, redirect to login
            await authStore.logout()
            await router.push({ name: "login" })
            throw e
          } finally {
            refreshPromise = null
          }
        }
      }

      // Parse error message from response for all 4xx errors (client errors)
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.detail ||
          error.response.statusText ||
          `Request failed with status code ${error.response.status}`

        // Create an ApiError with response and config
        throw new ApiError(errorMessage, error.response, error.config)
      }

      throw error
    },
  )

  return instance
}

/**
 * Default query axios instance
 */
export const queryAxios = createQueryAxios()

/**
 * Generic fetch function for TanStack Query
 */
export async function queryFetch<T = any>(
  url: string,
  config?: InternalAxiosRequestConfig,
): Promise<T> {
  const response = await queryAxios.request<T>({
    url,
    ...config,
    headers: config?.headers,
  } as InternalAxiosRequestConfig)
  return response.data
}

/**
 * GET request helper
 */
export async function queryGet<T = any>(
  url: string,
  config?: Partial<InternalAxiosRequestConfig>,
): Promise<T> {
  return queryFetch<T>(url, { ...config, method: "GET", headers: config?.headers as AxiosRequestHeaders })
}

/**
 * POST request helper
 */
export async function queryPost<T = any>(
  url: string,
  data?: any,
  config?: Partial<InternalAxiosRequestConfig>,
): Promise<T> {
  return queryFetch<T>(url, { ...config, method: "POST", data, headers: config?.headers as AxiosRequestHeaders })
}

/**
 * PUT request helper
 */
export async function queryPut<T = any>(
  url: string,
  data?: any,
  config?: Partial<InternalAxiosRequestConfig>,
): Promise<T> {
  return queryFetch<T>(url, { ...config, method: "PUT", data, headers: config?.headers as AxiosRequestHeaders })
}

/**
 * DELETE request helper
 */
export async function queryDelete<T = any>(
  url: string,
  config?: Partial<InternalAxiosRequestConfig>,
): Promise<T> {
  return queryFetch<T>(url, { ...config, method: "DELETE", headers: config?.headers as AxiosRequestHeaders })
}

/**
 * PATCH request helper
 */
export async function queryPatch<T = any>(
  url: string,
  data?: any,
  config?: Partial<InternalAxiosRequestConfig>,
): Promise<T> {
  return queryFetch<T>(url, { ...config, method: "PATCH", data, headers: config?.headers as AxiosRequestHeaders })
}

/**
 * POST request helper for FormData (file uploads)
 */
export async function queryPostFormData<T = any>(
  url: string,
  formData: FormData,
  config?: Partial<InternalAxiosRequestConfig>,
): Promise<T> {
  return queryFetch<T>(url, {
    ...config,
    method: "POST",
    data: formData,
    headers: {
      ...config?.headers,
      "Content-Type": "multipart/form-data",
    } as AxiosRequestHeaders,
  })
}
